<script setup lang="ts">
import { useExtendedI18n } from '@/i18n'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useFilter } from '@/modules/core/composables/useFilter'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import IndividualLayout from '@/modules/investing/components/individuals/individual-layout.vue'
import VSelectInvestor from '@/modules/investing/components/VSelectInvestor.vue'
import { individual_invoice_columns } from '@/modules/investing/config/columns'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'
import { useInvoiceStore } from '@/modules/investing/stores/invoice-store'
import { investorPath } from '@/modules/investing/utils/investor'
import {
  ActionItem,
  ActionsGroup,
  ActionsMenu,
  VBadge,
  VButton,
  VButtonGroup,
  VModal,
  VSection,
  VTable,
  VTextArea,
  VTextField,
} from '@/modules/shared/components'
import { useModal } from '@/modules/shared/composables/use-modal'
import { useActionsMenu } from '@/modules/shared/utils/actions-menu'
import { createQueryString } from '@/modules/shared/utils/routing'
import { format } from '@/modules/shared/utils/v-table'
import { get, has } from 'lodash'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch as useFetchV3 } from '@/modules/shared/composables/use-fetch'
import { required } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'
import InvoiceWrapper from '@/modules/investing/components/InvoiceWrapper.vue'

const { n } = useExtendedI18n()
const route = useRoute()

const updateURLQuery = () => {
  const query = {}
  if (filters.value.type) {
    query.tab = filters.value.type
  }
  if (selectedCids.value.length !== individualStore.listShareholders.length) {
    query.investor = selectedCids.value.join(',')
  }
  const queryString = createQueryString(query)
  window.history.replaceState(null, null, `?${queryString}`)
}

///////////////////////////////////////////////////////////////////////////////
// Calls
///////////////////////////////////////////////////////////////////////////////

const authStore = useAuthStore()
const individualStore = useIndividualStore()
const invoiceStore = useInvoiceStore()
const individual = computed(() => individualStore.individual)

const selectedCids = computed(() =>
  individualStore.selectedShareholderKeys.map((keys) => JSON.parse(keys).reverse().join(':')),
)

const { filters, setFilter } = useFilter({
  type: null,
})

const filtered_invoices = computed(() => {
  return individualStore.listInvoices.filter((invoice) => {
    if (filters.value.type === 'outstanding') {
      return !invoice.marked_as_paid
    } else if (filters.value.type === 'paid') {
      return invoice.marked_as_paid
    }
    return true
  })
})

const outstanding_invoice_ids = computed(() =>
  individualStore.listInvoices.filter((invoice) => !invoice.marked_as_paid).map((invoice) => invoice.id),
)
const paid_invoice_ids = computed(() =>
  individualStore.listInvoices.filter((invoice) => invoice.marked_as_paid).map((invoice) => invoice.id),
)

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const { individual_id, slug } = route.params as { individual_id: string; slug: string }

const fetch = async (shareholder_cids = null) => {
  showSkeleton()
  await individualStore.fetchIndividual(individual_id, { slug, shareholder_cids })

  if (shareholder_cids === null)
    shareholder_cids = individualStore.listShareholders.map((shareholder) => `${shareholder.type}:${shareholder.id}`)
  await Promise.all([individualStore.fetchInvoices(individual_id, { slug, shareholder_cids })])
  hideSkeleton()
}

const markInvoiceAsUnpaid = async (invoice) => {
  const confirm = window.confirm('Are you sure you want to mark the invoice as unpaid?')
  if (!confirm) return

  actionsMenu.setLoading('mark_as_unpaid', true)
  await invoiceStore.markMultipleInvoicesAsUnpaid([invoice.id])
  await fetch(selectedCids.value)
  actionsMenu.setLoading('mark_as_unpaid', false)
}

const markInvoicesAsUnpaid = async () => {
  const invoice_ids = selectedInvoiceIds.value.filter((invoice_id) => paid_invoice_ids.value.includes(invoice_id))
  if (invoice_ids.length === 0) return alert('Please select at least one paid invoice to mark as unpaid')

  const confirm = window.confirm('Are you sure you want to mark the selected invoices as unpaid?')
  if (!confirm) return

  loading.value = true
  await invoiceStore.markMultipleInvoicesAsUnpaid(invoice_ids)
  await fetch(selectedCids.value)
  loading.value = false

  selectedInvoiceIds.value = []
}

const removeInvoice = async (invoice) => {
  const confirm = window.confirm('Are you sure you want to delete the invoice?')
  if (!confirm) return

  actionsMenu.setLoading('delete', true)
  await useFetchV3(`/${slug}/investing/invoice/${invoice.id}/remove`).delete().json<{}>()
  await fetch(selectedCids.value)
  actionsMenu.setLoading('delete', false)
}

const sendReminder = async (invoice) => {
  actionsMenu.setLoading('send_reminder', true)
  await useFetchV3(`/${slug}/investing/invoice/${invoice.id}/send_reminder`).post().json<{}>()
  actionsMenu.setLoading('send_reminder', false)
}

const submitInvoicePaymentForm = async () => {
  invoicePaymentForm.value.invoice_ids = selectedInvoiceIds.value.filter((invoice_id) =>
    outstanding_invoice_ids.value.includes(invoice_id),
  )
  const valid = await v$.value.$validate()
  if (!valid) return

  loading.value = true
  await invoiceStore.markMultipleInvoicesAsPaid(invoicePaymentForm.value)
  await fetch(selectedCids.value)
  loading.value = false
  invoicePaymentFormModal.close()

  // reset
  invoicePaymentForm.value = { ...initialState }
  selectedInvoiceIds.value = []
}

const onFilter = async () => {
  await fetch(selectedCids.value)
}

const invoiceCallback = async () => {
  await fetch(selectedCids.value)
}

///////////////////////////////////////////////////////////////////////////////
// Authorization
///////////////////////////////////////////////////////////////////////////////

const is_viewing_own_profile = computed(() => individual_id === authStore.current_user.investor_id?.toString())
const isAdmin = computed(() => authStore.is_site_or_group_admin)

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()
const actionsMenu = useActionsMenu(['send_reminder', 'mark_as_paid', 'mark_as_unpaid', 'delete'])

const selectedInvoiceIds = ref<number[]>([])
const mark_single_invoice = ref(false)
const invoicePaymentFormModal = useModal()
const loading = ref(false)
const pdfLoading = ref(false)

const downloadPdf = async (invoice: any) => {
  if (pdfLoading.value) return
  pdfLoading.value = true
  try {
    const baseUrl = import.meta.env.VITE_VENTURE360_API_URL.replace('/v3', '')
    const url = `${baseUrl}/v4/${slug}/investing/invoices/${invoice.id}/pdf`
    const response = await window.fetch(url, {
      headers: { Authorization: `Bearer ${useAuthStore().access_token}` },
    })
    const blob = await response.blob()
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `invoice-${invoice.id}.pdf`
    link.click()
    URL.revokeObjectURL(link.href)
  } finally {
    pdfLoading.value = false
  }
}

const selectInvoiceType = (type: string | null) => {
  setFilter('type', type)
  updateURLQuery()
}
const toggleInvoiceSelection = (id: number) => {
  mark_single_invoice.value = false
  if (selectedInvoiceIds.value.includes(id)) {
    selectedInvoiceIds.value = selectedInvoiceIds.value.filter((invoiceId) => invoiceId !== id)
  } else {
    selectedInvoiceIds.value.push(id)
  }
}

// Form
const initialState = {
  invoice_ids: [],
  paid_at: null,
  instructions: null,
}
const rules = {
  invoice_ids: { required },
  paid_at: { required },
}
const invoicePaymentForm = ref({ ...initialState })
const v$ = useVuelidate(rules, invoicePaymentForm, { $lazy: true })

const markInvoiceAsPaid = (invoice) => {
  mark_single_invoice.value = true
  selectedInvoiceIds.value = [invoice.id]
  invoicePaymentFormModal.open()
}
const openInvoicePaymentForm = () => {
  mark_single_invoice.value = false
  const invoice_ids = selectedInvoiceIds.value.filter((invoice_id) =>
    outstanding_invoice_ids.value.includes(invoice_id),
  )
  if (invoice_ids.length === 0) return alert('Please select at least one outstanding invoice to mark as paid')
  invoicePaymentFormModal.open()
}

let hasInvoiceAlreadyOpened = false
const isInvoiceOpenedByDefault = (invoice_id) => {
  if (hasInvoiceAlreadyOpened) return null
  return route.query.invoice_id?.toString() === invoice_id.toString()
}
const onOpenInvoiceDetails = (invoice_id) => {
  hasInvoiceAlreadyOpened = true
}

onMounted(async () => {
  await fetch()
  if (route.query.tab) setFilter('type', route.query.tab)
})
</script>

<template>
  <IndividualLayout selectedTab="invoices">
    <VSection class="flex items-center justify-between">
      <VButtonGroup>
        <VSelectInvestor
          v-model="individualStore.selectedShareholderKeys"
          :investors="individualStore.listShareholders"
          :onClose="onFilter"
        />
        <VButton :active="filters.type === null" class="w-32" @click="selectInvoiceType(null)"> All </VButton>
        <VButton :active="filters.type === 'outstanding'" class="w-32" @click="selectInvoiceType('outstanding')">
          Outstanding
        </VButton>
        <VButton :active="filters.type === 'paid'" class="w-32" @click="selectInvoiceType('paid')"> Paid </VButton>
      </VButtonGroup>
      <VButtonGroup v-if="isAdmin">
        <VButton size="sm" variant="v-blue" :loading="loading" :click="markInvoicesAsUnpaid"> Mark as Unpaid </VButton>
        <VButton size="sm" variant="v-blue" :disabled="loading" :click="openInvoicePaymentForm"> Mark as Paid </VButton>
      </VButtonGroup>
    </VSection>
    <VTable
      :columns="individual_invoice_columns(isAdmin)"
      :items="filtered_invoices"
      :name="`Investor-${individual_id}-invoices`"
      :skeleton="skeleton"
      :slots="['actions', 'investor.name', 'name', 'marked_as_paid', 'checkbox']"
    >
      <template #checkbox="{ item: invoice }">
        <input
          type="checkbox"
          :checked="selectedInvoiceIds.includes(invoice.id) && !mark_single_invoice"
          @change="toggleInvoiceSelection(invoice.id)"
        />
      </template>
      <template #investor.name="{ item: invoice }">
        <RouterLink
          v-if="isAdmin"
          class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
          :to="investorPath(get(invoice, 'investor._cid'))"
        >
          {{ get(invoice, 'investor.name') }}
        </RouterLink>
        <span v-else>{{ get(invoice, 'investor.name') }}</span>
      </template>
      <template #name="{ item: invoice }">
        <InvoiceWrapper
          :invoiceId="invoice.id"
          :status="invoice.display_status"
          :callback="invoiceCallback"
          :onOpenDetails="() => onOpenInvoiceDetails(invoice.id)"
          :isOpenByDefault="isInvoiceOpenedByDefault(invoice.id)"
          :enable_links="true"
        >
          <template #label>
            <div class="hyperlink">
              {{ get(invoice, 'name') }}
            </div>
          </template>
        </InvoiceWrapper>
      </template>
      <template #marked_as_paid="{ item: invoice }">
        {{ invoice.marked_as_paid ? 'paid' : 'outstanding' }}
      </template>
      <template #actions="{ item: invoice }">
        <div class="flex items-center justify-end space-x-1.5">
          <ActionsMenu v-if="isAdmin">
            <ActionsGroup>
              <ActionItem
                v-if="!invoice.marked_as_paid"
                text="Send Reminder"
                @click="() => sendReminder(invoice)"
                :loading="actionsMenu.actions.value.send_reminder?.loading"
              />
              <ActionItem
                v-if="!invoice.marked_as_paid"
                text="Mark as Paid"
                @click="() => markInvoiceAsPaid(invoice)"
              />
              <ActionItem
                v-else
                text="Mark as Unpaid"
                @click="() => markInvoiceAsUnpaid(invoice)"
                :loading="actionsMenu.actions.value.mark_as_unpaid?.loading"
              />
              <ActionItem
                text="Delete"
                @click="() => removeInvoice(invoice)"
                :loading="actionsMenu.actions.value.delete?.loading"
              />
            </ActionsGroup>
          </ActionsMenu>
        </div>
      </template>
      <template #entity.name="{ item }">
        <div
          v-if="
            get(item, 'entity.entity_type') && ['fund', 'spv'].includes(get(item, 'entity.entity_type')?.toLowerCase())
          "
        >
          {{ get(item, 'entity.name') }}
        </div>
      </template>
    </VTable>
    <VModal :modalStore="invoicePaymentFormModal">
      <template #main>
        <VSection :label="mark_single_invoice ? 'Mark invoice as paid' : 'Mark multiple invoices as paid'">
          <form @submit.prevent="submitInvoicePaymentForm">
            <div class="mt-6 space-y-3">
              <VTextField
                v-model="invoicePaymentForm.paid_at"
                label="Payment Date"
                property="paid_at"
                ref="autofocus"
                type="date"
                :v$="v$"
              />
              <VTextArea
                label="Intructions / Notes"
                v-model="invoicePaymentForm.instructions"
                :v$="v$"
                property="instructions"
              />
            </div>
          </form>
        </VSection>
      </template>
      <template #footer>
        <div class="flex items-center justify-between space-x-3">
          <VButton :click="invoicePaymentFormModal.close" size="lg">Close</VButton>
          <VButton :click="submitInvoicePaymentForm" class="w-full" size="lg" :loading="loading" variant="primary">
            Submit
          </VButton>
        </div>
      </template>
    </VModal>
  </IndividualLayout>
</template>

<style scoped>
table td {
  @apply whitespace-nowrap pb-2 pr-5  first:w-[1%];
}
table td:nth-child(1) {
  @apply text-left;
}
table td:nth-child(2) {
  @apply text-right;
}
</style>
